/* eslint-disable import/no-relative-packages */
import {
  Environment,
  getMicroFrontends,
  MondrianDeploymentType,
  MondrianInstance,
  SharedDependencyId,
  SharedDependencyVersion,
} from '../../../config';
import { readOverridesFromMagicUrl } from '../importmapOverrides';
import { BrowserSupport, getBrowserSupport } from './browserSupport';
import { getEnvironment } from './environment';

import {
  getDeprecatedSharedDependencyConfig,
  getEntrypoint,
} from './sharedDependencies';

declare global {
  interface Window {
    importMapOverrides?: {
      getOverrideMap: () => System.ImportMap;
      getCurrentPageMap: () => Promise<System.ImportMap>;
      resetOverrides: () => void;
      addOverride: (key: string, value: string) => void;
    };
  }
}

/**
 * Returns true if the import map can be overridden.
 */
export const allowOverrides = (instance: MondrianInstance): boolean =>
  instance.hostname.allowImportMapOverrides;

/**
 * Returns the import map for the current environment for the given instance.
 */
export const generateMicroFrontendImportMap = (
  instance: MondrianInstance
): System.ImportMap => {
  const deploymentUrls = new Array<[string, string]>();

  getMicroFrontends(instance).forEach((mfe) => {
    const deploymentUrl =
      instance.deployment === MondrianDeploymentType.AMPLIFY
        ? mfe.deploymentUrls.web[getEnvironment()].main
        : mfe.deploymentUrls.bundle.main;
    deploymentUrls.push([mfe.packageName, deploymentUrl]);

    if (mfe.providesWidgets) {
      const widgetDeploymentUrl =
        instance.deployment === MondrianDeploymentType.AMPLIFY
          ? mfe.deploymentUrls.web[getEnvironment()].widgets
          : mfe.deploymentUrls.bundle.widgets;
      deploymentUrls.push([`${mfe.packageName}/widgets`, widgetDeploymentUrl]);
    }
  });

  return {
    imports: Object.fromEntries(deploymentUrls),
  };
};

/**
 * Adds the given suffix to the given url right before the extension,
 * if it is a javascript file.
 * @param url
 * @param suffix
 */
export const addSuffixIfJavaScriptUrl = (
  url: string,
  suffix: string
): string => {
  const extension = url.split('.').pop();
  // only add it to javascript urls
  if (extension !== 'js') {
    return url;
  }
  return `${url.slice(0, -extension.length - 1)}${suffix}.${extension}`;
};

/**
 * Adds the given suffix to all javascript urls in the given import map.
 * @param importMap
 * @param suffix
 */
export const addSuffixToImportMapJavaScriptUrls = (
  importMap: System.ImportMap,
  suffix: string
): System.ImportMap => {
  return {
    imports: Object.fromEntries(
      Object.entries(importMap.imports ?? {}).map(([packageName, url]) => [
        packageName,
        addSuffixIfJavaScriptUrl(url, suffix),
      ])
    ),
  };
};

/**
 * Returns the scoped override key for the given microfrontend name. It can be used
 * in the scopes section of an import-map as key to override all imports for a specific
 * microfrontend.
 * @param microFrontendName
 * @param importMap
 * @param deploymentType
 */
export const getScopedOverrideKey = (
  microFrontendName: string,
  importMap: System.ImportMap,
  deploymentType: MondrianDeploymentType
): string | null => {
  const microFrontendImport = importMap.imports[microFrontendName];

  if (!microFrontendImport) {
    throw new Error(
      `Could not find import for ${microFrontendName} in importmap`
    );
  }

  if (deploymentType === MondrianDeploymentType.AMPLIFY) {
    const { protocol, host } = new URL(microFrontendImport);
    return `${protocol}//${host}/`;
  }

  if (deploymentType === MondrianDeploymentType.BUNDLE) {
    const { protocol, host } = window.location;
    return `${protocol}//${host}/${microFrontendName}/`;
  }

  throw new Error(`Unknown deployment type: ${String(deploymentType)}`);
};

/**
 * Adds scoped dependency overrides to the given import map, to serve deprecated
 * shared dependencies for microfrontends that depend on them. This is based on the
 * ../generated/deprecated-shared-dependencies.json which is automatically generated.
 * @param instance
 * @param importMap
 */
export const addDeprecatedSharedDependencyScopedOverrides = (
  instance: MondrianInstance,
  importMap: System.ImportMap
): System.ImportMap => {
  const scopes: System.ImportMap['scopes'] = {};
  Object.entries(getDeprecatedSharedDependencyConfig())
    .filter(([microFrontendPackageName]) =>
      getMicroFrontends(instance).some(
        (microfrontend) =>
          microfrontend.packageName === microFrontendPackageName
      )
    )
    .forEach(([microFrontendName, sharedDependencyList]) => {
      const key = getScopedOverrideKey(
        microFrontendName,
        importMap,
        instance.deployment
      );

      const deprecatedSharedDependencyScopes: Partial<
        Record<SharedDependencyId, SharedDependencyVersion>
      > = Object.fromEntries(
        Object.entries(sharedDependencyList)
          // filter out shared dependencies that should serve mondrians version
          .filter(([, { mondriansVersionServed }]) => !mondriansVersionServed)
          // map the dependency name to its local entrypoint
          .map(([sharedDependencyName, { deprecatedVersionServed }]) => [
            sharedDependencyName,
            getEntrypoint(
              sharedDependencyName as SharedDependencyId,
              deprecatedVersionServed
            ),
          ])
      );

      scopes[key] = {
        ...(scopes[key] ?? {}),
        ...deprecatedSharedDependencyScopes,
      };
    });

  return { ...importMap, scopes };
};

/**
 * Loads the micro frontend import map(s) for the given instance and adds scopes
 * and overrides if necessary.
 * @param instance
 */
// eslint-disable-next-line complexity
export const loadMicroFrontendImportMap = (
  instance: MondrianInstance
): void => {
  const { addImportMap } = System;
  const browserSupport = getBrowserSupport();
  let importMap = generateMicroFrontendImportMap(instance);

  if (browserSupport !== BrowserSupport.ES6_MODULES) {
    importMap = addSuffixToImportMapJavaScriptUrls(
      importMap,
      browserSupport === BrowserSupport.MODERN ? '-modern' : '-legacy'
    );
  }

  const importMapWithScopedOverrides =
    addDeprecatedSharedDependencyScopedOverrides(instance, importMap);

  if (getEnvironment() === Environment.DEV) {
    // eslint-disable-next-line no-console
    console.debug(
      'This is your microfrontend-importmap',
      importMapWithScopedOverrides
    );
  }

  addImportMap(importMapWithScopedOverrides);

  if (allowOverrides(instance) && window.importMapOverrides) {
    const overrideMap = window.importMapOverrides.getOverrideMap();
    addImportMap(overrideMap);

    readOverridesFromMagicUrl();
  }
};
